const base62 = require('ya-base62');
const libphonenumber = require('google-libphonenumber');
const regionsToCountryCode = require('./phoneRegionData.json');

const {E164} = libphonenumber.PhoneNumberFormat;
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const phoneNumberFormat = /^\+[1-9][0-9]{0,14}$/;

/**
 * Encodes a phone number to base 62. The encoding decreases the phone number's
 * length by a factor of around 1.8.
 * @param {string} phoneNumber phone number to encode in '+491705005050' format
 *    with equal or less than 16 digits at max including '+'.
 * @returns {string} Encoded base 62 phone number without leading '+'.
 */
const encode = phoneNumber => {
  if (!phoneNumberFormat.test(phoneNumber)) {
    throw new RangeError(`Phone number ${phoneNumber} has invalid format`);
  }

  // remove leading '+' make to a number
  const value = Number(phoneNumber.slice(1));
  return base62.encode(value);
};

/**
 * Decodes a base 62 encoded phone number.
 * @param {string} phoneNumberBase62 phone number without leading '+' to decode
 *    from base 62. Chiffre should have been encoded with #encodePhoneNumber.
 * @returns {string} Decoded phone number.
 */
const decode = phoneNumberBase62 => {
  const value = base62.decode(phoneNumberBase62);
  return `+${value}`;
};

/**
 * Validates a phone number and transforms it to E.164 format.
 * @param {string} phoneNumber phone number starting with country code in + format.
 * @returns {Object} Contains isValid property as well as phoneNumberE164 property
 *    if and only if isValid is true.
 */
const transformToE164 = phoneNumber => {
  let e164 = {isValid: false};
  try {
    const phoneNumberParsed = phoneUtil.parse(phoneNumber.trim());
    if (phoneUtil.isValidNumber(phoneNumberParsed)) {
      const phoneNumberE164 = phoneUtil.format(phoneNumberParsed, E164);
      e164 = {isValid: true, phoneNumberE164};
    }
  } catch (e) { /* nothing to do */ }

  return e164;
};

const parse = phoneNumber => {
  const parsedNumber = null;
  try {
    const phoneNumberParsed = phoneUtil.parseAndKeepRawInput(phoneNumber.trim());
    return {
      regionCode: phoneUtil.getRegionCodeForNumber(phoneNumberParsed),
      countryCode: String(phoneNumberParsed.getCountryCode()),
      nationalNumber: String(phoneNumberParsed.getNationalNumber()),
      rawInputValue: String(phoneNumberParsed.getRawInput()),
    }
  } catch (e) { /* nothing to do */ }

  return parsedNumber;
}

const format = (phoneNumber, regionCode = 'de') => {
  const phoneNumberParsed = phoneUtil.parse(phoneNumber.trim(), regionCode);
  return phoneUtil.format(phoneNumberParsed, E164);
}

/** short for transformToE164.isValid
 * @param {string} phoneNumber phone number starting with country code in + format.
 * @returns {boolean} whether a transformation to E164 will be valid
 */
const transformableToE164 = phoneNumber => transformToE164(phoneNumber).isValid;

const getNumberType = phoneNumber => phoneUtil.getNumberType(phoneUtil.parseAndKeepRawInput(phoneNumber.trim()))

module.exports = {
  encode,
  decode,
  transformableToE164,
  transformToE164,
  parse,
  format,
  regionsToCountryCode,
  getNumberType,
};
